/**
 * PHI-access audit beacons (#185) — fire-and-forget pings to the logPhiAccess
 * Cloud Function, which appends HIPAA §164.312(b) audit entries recording who
 * viewed which patient / eligibility result / appointment day.
 *
 * Identity is stamped server-side from the verified auth token — the beacon
 * only declares WHAT was opened. Repeat views of the same resource are
 * deduped in-session (a slideout reopened five times is one trail entry),
 * and failures never surface to the UI: losing a beacon must not break
 * patient care workflows.
 */
import { httpsCallable } from 'firebase/functions'
import * as Sentry from '@sentry/react'
import { auth, functions } from '../config'

export type PhiViewResource =
  | 'patient'
  | 'eligibility_result'
  | 'appointment_day'
  // The Recovery Loop drawer's thread view, keyed by the case's check id — a
  // distinct type so message reads are distinguishable from check-result views
  // (and don't dedup-collide with them for the same check id in one session).
  | 'sms_conversation'

/** Resources already logged this browser session (`uid:type:id`). */
const seenViews = new Set<string>()

/** Fraction of beacon failures reported — enough signal, no error-quota flood. */
const FAILURE_SAMPLE_RATE = 0.05

function send(payload: Record<string, unknown>): Promise<void> {
  // httpsCallable can throw synchronously (e.g. a misconfigured functions
  // instance). The module invariant is that NO beacon failure ever reaches a
  // React handler, so a sync throw is routed into the same rejection path the
  // callers already .catch().
  try {
    return httpsCallable(functions, 'logPhiAccess')(payload).then(() => undefined)
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Beacon failures never surface to the user — losing one must not break
 * patient care. But a *systematically* dead beacon (deploy-order gap, CORS,
 * auth rejection) would zero the entire view trail silently, so a sample of
 * failures goes to Sentry. View beacons sample at 5% (high volume, and a
 * reopen retries them); staff-edit beacons report at 100% — their volume is
 * tiny, there is no retry path, and each one lost is a hole in the oversight
 * trail.
 */
function reportBeaconFailure(err: unknown, sampleRate = FAILURE_SAMPLE_RATE): void {
  if (Math.random() < sampleRate) {
    Sentry.captureException(err, { tags: { beacon: 'logPhiAccess' } })
  }
}

/** Record that a PHI surface was opened. Deduped per resource per session. */
export function logPhiView(
  resourceType: PhiViewResource,
  resourceId: string,
  clinicId?: string | null,
): void {
  if (!resourceId) return
  // The uid is part of the key: two people sharing a front-desk browser must
  // each leave their own trail, never inherit the other's "already logged".
  const key = `${auth.currentUser?.uid ?? ''}:${resourceType}:${resourceId}:${clinicId ?? ''}`
  if (seenViews.has(key)) return
  seenViews.add(key)

  send({ kind: 'view', resourceType, resourceId, ...(clinicId ? { clinicId } : {}) })
    .catch((err: unknown) => {
      // Not logged after all — un-mark so reopening the chart tries again,
      // rather than one dropped request silently un-logging it for the session.
      seenViews.delete(key)
      reportBeaconFailure(err)
    })
}

// ── Staff edit-mode write beacon ─────────────────────────────────────────
// Direct client Firestore writes under an edit-mode impersonation session
// bypass callables (the isStaffEditing rules path), so the write helpers in
// src/services/* report them here. The backend accepts a staff_edit event
// only from a verified edit-mode impersonation token — a tenant session
// calling logStaffEdit is a no-op client-side (flag off) and would be
// rejected server-side anyway.

let staffEditSessionActive = false

/** Toggled by the Dashboard when an edit-mode impersonation session is on. */
export function setStaffEditSession(active: boolean): void {
  staffEditSessionActive = active
}

/** Record one direct Firestore write made during a staff edit session. */
export function logStaffEdit(
  collection: string,
  docId: string,
  writeAction: 'create' | 'update' | 'delete',
): void {
  if (!staffEditSessionActive) return
  send({ kind: 'staff_edit', collection, docId, writeAction })
    .catch((err: unknown) => reportBeaconFailure(err, 1))
}
